import { useEffect, useRef } from 'react';
import vercelAnalytics from '../services/vercelAnalyticsService';

/**
 * Hook para monitoramento de performance integrado com Speed Insights
 * Complementa o Speed Insights automático com métricas customizadas
 */
export const usePerformanceMonitoring = (pageName, options = {}) => {
    const pageStartTime = useRef(Date.now());
    const performanceObserver = useRef(null);
    const {
        trackCustomMetrics = true,
        trackResources = false,
        trackUserTiming = false
    } = options;

    useEffect(() => {
        const startTime = pageStartTime.current;

        // Track page load time
        const handlePageLoad = () => {
            const loadTime = Date.now() - startTime;
            const deviceInfo = vercelAnalytics.getDeviceInfo();
            
            vercelAnalytics.trackPageLoadTime(
                pageName, 
                loadTime, 
                deviceInfo.device,
                {
                    navigationTiming: getNavigationTiming(),
                    connectionType: getConnectionType()
                }
            );
        };

        // Track Web Vitals if available and custom tracking enabled
        if (trackCustomMetrics && 'PerformanceObserver' in window) {
            try {
                // Largest Contentful Paint
                const lcpObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const lastEntry = entries[entries.length - 1];
                    if (lastEntry) {
                        vercelAnalytics.trackWebVitals({
                            lcp: lastEntry.startTime,
                            page: pageName
                        });
                    }
                });
                lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

                // First Input Delay
                const fidObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach((entry) => {
                        vercelAnalytics.trackWebVitals({
                            fid: entry.processingStart - entry.startTime,
                            page: pageName
                        });
                    });
                });
                fidObserver.observe({ entryTypes: ['first-input'] });

                // Cumulative Layout Shift
                let clsValue = 0;
                const clsObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (!entry.hadRecentInput) {
                            clsValue += entry.value;
                        }
                    }
                    vercelAnalytics.trackWebVitals({
                        cls: clsValue,
                        page: pageName
                    });
                });
                clsObserver.observe({ entryTypes: ['layout-shift'] });

                performanceObserver.current = [lcpObserver, fidObserver, clsObserver];
            } catch (error) {
                console.warn('Performance Observer not supported:', error);
            }
        }

        // Track resource loading if enabled
        if (trackResources && 'PerformanceObserver' in window) {
            try {
                const resourceObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach((entry) => {
                        // Only track significant resources
                        if (entry.duration > 100) {
                            vercelAnalytics.trackResourcePerformance(
                                getResourceType(entry.name),
                                entry.duration,
                                entry.transferSize
                            );
                        }
                    });
                });
                resourceObserver.observe({ entryTypes: ['resource'] });
                
                if (performanceObserver.current) {
                    performanceObserver.current.push(resourceObserver);
                } else {
                    performanceObserver.current = [resourceObserver];
                }
            } catch (error) {
                console.warn('Resource Performance Observer not supported:', error);
            }
        }

        // Handle page load
        if (document.readyState === 'complete') {
            handlePageLoad();
        } else {
            window.addEventListener('load', handlePageLoad);
        }

        // Cleanup
        return () => {
            if (performanceObserver.current) {
                performanceObserver.current.forEach(observer => {
                    try {
                        observer.disconnect();
                    } catch (error) {
                        console.warn('Error disconnecting performance observer:', error);
                    }
                });
            }
            window.removeEventListener('load', handlePageLoad);
        };
    }, [pageName, trackCustomMetrics, trackResources]);

    // Utility functions
    const getNavigationTiming = () => {
        if (!window.performance || !window.performance.timing) return {};
        
        const timing = window.performance.timing;
        return {
            dns: timing.domainLookupEnd - timing.domainLookupStart,
            tcp: timing.connectEnd - timing.connectStart,
            ttfb: timing.responseStart - timing.navigationStart,
            domLoading: timing.domLoading - timing.navigationStart,
            domInteractive: timing.domInteractive - timing.navigationStart,
            domComplete: timing.domComplete - timing.navigationStart
        };
    };

    const getConnectionType = () => {
        if ('connection' in navigator) {
            return navigator.connection.effectiveType || 'unknown';
        }
        return 'unknown';
    };

    const getResourceType = (url) => {
        if (url.includes('.js')) return 'javascript';
        if (url.includes('.css')) return 'stylesheet';
        if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) return 'image';
        if (url.includes('.woff') || url.includes('.ttf')) return 'font';
        if (url.includes('/api/')) return 'api';
        return 'other';
    };

    // Return utility functions for manual tracking
    return {
        trackCustomEvent: (eventName, duration, metadata = {}) => {
            vercelAnalytics._track(`Performance ${eventName}`, {
                page: pageName,
                duration: Math.round(duration),
                ...metadata
            });
        },
        
        measureFunction: (fn, eventName) => {
            return async (...args) => {
                const start = performance.now();
                try {
                    const result = await fn(...args);
                    const duration = performance.now() - start;
                    
                    vercelAnalytics._track(`Function Performance`, {
                        function: eventName,
                        page: pageName,
                        duration: Math.round(duration),
                        success: true
                    });
                    
                    return result;
                } catch (error) {
                    const duration = performance.now() - start;
                    
                    vercelAnalytics._track(`Function Performance`, {
                        function: eventName,
                        page: pageName,
                        duration: Math.round(duration),
                        success: false,
                        error: error.message.substring(0, 100)
                    });
                    
                    throw error;
                }
            };
        }
    };
};

/**
 * Hook simples para páginas que só precisam de tracking básico
 */
export const useBasicPerformanceTracking = (pageName) => {
    return usePerformanceMonitoring(pageName, {
        trackCustomMetrics: true,
        trackResources: false,
        trackUserTiming: false
    });
};

/**
 * Hook completo para páginas críticas que precisam de monitoramento detalhado
 */
export const useAdvancedPerformanceTracking = (pageName) => {
    return usePerformanceMonitoring(pageName, {
        trackCustomMetrics: true,
        trackResources: true,
        trackUserTiming: true
    });
};